import { useId, useMemo, useState } from 'react';

/**
 * An email input that finishes the address for you.
 *
 * Typing a full address on a phone keyboard is twenty-odd taps, most of them
 * spent on a domain the person has typed a thousand times. Once there is an `@`
 * the set of likely endings is small and known, so the field offers them and a
 * tap completes the rest.
 *
 * The suggestions are a plain list rendered under the field rather than a
 * `<datalist>`: browser support for styling one is uneven, phone browsers often
 * ignore it entirely, and it cannot be filtered against what has been typed
 * after the `@`.
 */

/*
 * Ordered roughly by how often they are met in this product's market, since the
 * list is cut to a handful and the first entries are the ones that get seen.
 */
const DOMAINS = [
  'gmail.com',
  'hotmail.com',
  'outlook.com',
  'yahoo.com',
  'icloud.com',
  'live.com',
  'protonmail.com',
  'me.com',
  'aol.com',
  'yandex.com',
] as const;

const MAX_SUGGESTIONS = 4;

/** What to offer for a half-written address, or nothing when it is complete. */
export function suggestEmails(value: string): string[] {
  const at = value.indexOf('@');
  if (at <= 0) return [];

  // A second @ is a typo, not something to complete.
  if (value.indexOf('@', at + 1) !== -1) return [];

  const local = value.slice(0, at);
  const typed = value.slice(at + 1).toLowerCase();

  const matches = DOMAINS.filter((domain) => domain.startsWith(typed) && domain !== typed);

  return matches.slice(0, MAX_SUGGESTIONS).map((domain) => `${local}@${domain}`);
}

export function EmailField({
  id,
  value,
  onChange,
  autoComplete = 'username',
  required,
  autoFocus,
  placeholder,
  disabled,
}: {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  autoComplete?: string;
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  disabled?: boolean;
}) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [focused, setFocused] = useState(false);

  const suggestions = useMemo(
    () => (focused && !disabled ? suggestEmails(value) : []),
    [focused, disabled, value],
  );

  return (
    <div className="email-field">
      <input
        id={inputId}
        type="email"
        inputMode="email"
        autoComplete={autoComplete}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        required={required}
        disabled={disabled}
        autoFocus={autoFocus}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onFocus={() => setFocused(true)}
        // Delayed, or the blur beats the click and the suggestion is never taken.
        onBlur={() => window.setTimeout(() => setFocused(false), 150)}
      />

      {suggestions.length > 0 && (
        <ul className="email-suggestions">
          {suggestions.map((suggestion) => {
            const domain = suggestion.slice(suggestion.indexOf('@'));
            const local = suggestion.slice(0, suggestion.indexOf('@'));
            return (
              <li key={suggestion}>
                <button
                  type="button"
                  // Fires before blur, so the value is set even on a phone.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange(suggestion);
                    setFocused(false);
                  }}
                >
                  <span className="muted">{local}</span>
                  <strong>{domain}</strong>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
